import { useCallback, useMemo, useState } from 'react';
import {
  ContextMenu,
  ContextMenuBuilder,
  type ButtonType,
  type RepeatRowsPayload
} from '@/components/custom/erp/input-table-v2';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { ChiTietHoaDonBanHangDichVu } from '@/types/schemas/hoa-don-ban-dich-vu.type';
import { ErpInputTableV2, useInputTableValidation } from '@/components/custom/erp';
import { constraintKey, constraintValues } from '@/lib/account-constraints';
import { getDetailTableColumns } from '../../../cols-definition';
import type { AccountConstraint } from '@/types/schemas';
import { SelectedCellInfo } from './useInputTableRows';
import { copyCellValue } from '@/lib/table-handlers';
import { useDetailPaste } from './useDetailPaste';
import { FormMode } from '@/types/form';

interface DetailTabProps {
  rows: ChiTietHoaDonBanHangDichVu[];
  selectedRowUuid: string | null;
  selectedCell?: SelectedCellInfo | null;
  handleRowClick: DataTableRowEventHandler<ChiTietHoaDonBanHangDichVu>;
  handleCellClick?: (params: DataTableRenderCellParams<BanHangRecord>) => void;
  handleAddRow: () => void;
  handleDeleteRow: () => void;
  handleCopyRow: () => void;
  handlePasteRow: (clipboardText?: string) => void | Promise<void>;
  handleMoveRow: (direction: 'up' | 'down') => void;
  handleCellValueChange: (rowUuid: string, field: string, newValue: BanHangValue) => void;
  onRepeatForRows: (payload: RepeatRowsPayload<BanHangRecord>) => void;
  formMode: FormMode;
  dienGiai: string;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<BanHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
  constraints?: AccountConstraint[];
}

const DETAIL_ACTION_ORDER: ButtonType[] = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'];
const noop = () => {};
const detail = (row: BanHangSearchParams) => ({
  ma_dv: row.ma_dv_data?.uuid || row.ma_dv || '',
  tk_dt: row.tk_dt_data?.uuid || row.ma_dv_data?.tk_dt_data?.uuid || row.tk_dt || '',
  so_luong: row.so_luong,
  ...constraintValues(row)
});

export default function DetailTab({
  rows,
  selectedRowUuid,
  selectedCell,
  handleRowClick,
  handleCellClick,
  handleAddRow,
  handleDeleteRow,
  handleCopyRow,
  handlePasteRow,
  handleMoveRow,
  handleCellValueChange,
  onRepeatForRows,
  formMode,
  dienGiai,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting,
  constraints = []
}: DetailTabProps) {
  const { cellError } = useInputTableValidation({
    rows,
    name: 'chi_tiet',
    map: detail,
    deps: constraintKey(constraints)
  });

  const columns = useMemo(
    () => getDetailTableColumns(handleCellValueChange, dienGiai, cellError),
    [cellError, handleCellValueChange, dienGiai]
  );

  useDetailPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: ChiTietHoaDonBanHangDichVu | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({ columnField, columnValue, columnValues });
        },
        onRepeatForRows,
        rowsCount: rows.length
      }),
    [columns, contextMenu, onRepeatForRows, rows.length]
  );
  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: DataTableRenderCellParams<ChiTietHoaDonBanHangDichVu>) => {
      handleCellClick?.(params);
      setContextMenu({
        position: { x: event.clientX, y: event.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [handleCellClick]
  );
  const tableActions = useMemo(
    () => ({
      add: handleAddRow,
      delete: handleDeleteRow,
      copy: handleCopyRow,
      paste: handlePasteRow,
      moveUp: () => handleMoveRow('up'),
      moveDown: () => handleMoveRow('down'),
      export: noop,
      pin: noop
    }),
    [handleAddRow, handleCopyRow, handleDeleteRow, handleMoveRow, handlePasteRow]
  );

  return (
    <>
      <ErpInputTableV2<ChiTietHoaDonBanHangDichVu>
        rows={rows}
        autoHeight
        bottomSelector='[data-arito-bottom-bar]'
        onRowClick={handleRowClick}
        onCellClick={handleCellClick}
        onCellContextMenu={handleCellContextMenu}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns}
        getRowId={row => row?.uuid || ''}
        pasteTarget={selectedCell}
        actionLoading={isPasting}
        actionsDisabled={formMode === 'view'}
        actions={tableActions}
        actionOrder={DETAIL_ACTION_ORDER}
      />
      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() =>
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          })
        }
      />
    </>
  );
}
